<!-- ============================================================
  IdCalendar.vue — full-month date picker used by 3 screens
  (จองคลาส / ตารางเรียน / จัดการการจอง).
  Faithful SFC port of `IdCalendar` from vue-newdesign/full-preview.js,
  using REAL dates (not the prototype's hardcoded 2026).
  Pick ANY date freely, but disable (un-pickable):
    • every Monday (disableMondays, default true) — date.getDay() === 1
    • holiday dates passed via :holiday-keys (key format 'YYYY-M-D')
    • any date BEFORE :min-date (optional) — used by customer booking to pass the
      earliest bookable day (today before the 08:00 same-day cutoff, else tomorrow).
      Pass :past-reason to explain WHY those days are blocked on that screen.
  Layout comes from global istar-pages.css (.cal-*) — no scoped CSS.

  Props:
    modelValue     String  selected date key 'YYYY-M-D'  (v-model)
    holidayKeys    Array   [string] disabled holiday keys
    markKeys       Array   [string] keys to show a dot under (e.g. days with bookings)
    disableMondays Boolean default true
    minDate        Date    optional — dates before this day are disabled (and the
                           calendar can't page back past its month)
    pastReason     String  optional — tooltip shown on days disabled by minDate
                           (default: the neutral calendar.tooltipBeforeMinDate)
    multi          Boolean default false — multi-select mode (จองหลายวัน): clicking a
                           day emits 'toggle-date' with its key instead of update:modelValue;
                           the PARENT owns the array and passes it back via :selected-keys
    selectedKeys   Array   [string] keys highlighted as selected (multi mode only)
  Emits: update:modelValue, toggle-date
============================================================ -->
<template>
  <div class="idcal">
    <div class="cal-nav">
      <span class="mdi mdi-chevron-left" :class="{ 'cal-nav-off': !canPrev }" @click="prev"></span>
      <button type="button" class="cal-nav-label" @click="togglePicker">
        {{ monthLabel }}<span class="mdi" :class="picker ? 'mdi-menu-up' : 'mdi-menu-down'"></span>
      </button>
      <span class="mdi mdi-chevron-right" @click="next"></span>
    </div>
    <!-- quick month/year picker (จิ้มที่ชื่อเดือนเพื่อเลือกเดือน/ปีได้เลย ไม่ต้องกด ‹ › ทีละเดือน) -->
    <div v-if="picker" class="cal-picker">
      <div class="cal-picker-yr">
        <span class="mdi mdi-chevron-left" :class="{ 'cal-nav-off': !canPrevYear }" @click="canPrevYear && pickYear--"></span>
        <span class="cal-picker-yr-lbl">{{ dispYear(pickYear) }}</span>
        <span class="mdi mdi-chevron-right" @click="pickYear++"></span>
      </div>
      <div class="cal-picker-grid">
        <button v-for="(m, i) in monthsShort" :key="i" type="button" class="cal-mon"
          :class="{ on: i === vm && pickYear === vy, disabled: monthDisabled(i) }"
          :disabled="monthDisabled(i)" @click="chooseMonth(i)">{{ m }}</button>
      </div>
    </div>
    <div class="cal-grid">
      <div v-for="d in dows" :key="d" class="cal-dow">{{ d }}</div>
      <template v-for="(c, i) in cells" :key="i">
        <div v-if="!c" class="cal-cell cal-empty"></div>
        <div
          v-else
          class="cal-cell"
          :class="{ on: multi ? selectedKeys.includes(c.key) : c.key === modelValue, disabled: c.disabled, today: c.today, holiday: c.holiday }"
          v-hint="c.holiday ? ($t('calendar.holidayLabel') + ': ' + c.holidayDesc) : (c.disabled ? (c.past ? (pastReason || $t('calendar.tooltipBeforeMinDate')) : c.mon ? $t('calendar.tooltipMondayClosed') : $t('calendar.tooltipHolidayClosed')) : '')"
          @click="pick(c)"
        >
          {{ c.n }}<span v-if="c.holiday" class="cal-dot cal-dot-hol"></span><span v-else-if="c.mark && !c.disabled" class="cal-dot"></span>
        </div>
      </template>
    </div>
    <!-- holidays in the displayed month (date + description) — shown on every calendar that
         passes :holidays, whether or not the days are disabled -->
    <div v-if="monthHolidays.length" class="cal-holidays">
      <div class="cal-holidays-h"><span class="mdi mdi-calendar-star"></span> {{ $t('calendar.holidaysThisMonth') }}</div>
      <div v-for="h in monthHolidays" :key="h.key" class="cal-hol-row">
        <span class="cal-hol-day">{{ h.day }}</span>
        <span class="cal-hol-desc">{{ h.description }}</span>
      </div>
    </div>
    <!-- legend only makes sense where Mondays are actually blocked (group-class booking, add-holiday);
         hidden on calendars that allow Mondays (course dates, DOB, Private booking) -->
    <div v-if="disableMondays" class="cal-legend"><span class="cal-legend-x">×</span> {{ $t('calendar.legendClosed') }}</div>
  </div>
</template>

<script>
// month/weekday names + the header label follow the app language (formatMonthYear,
// weekdayShortNames, monthShortNames read i18nState.locale), so the calendar re-renders
// localized when the user switches language; displayYear adds the Buddhist era for Thai only.
import { formatMonthYear, weekdayShortNames, monthShortNames, displayYear } from '../../utils/locale';
const keyOf = (d) => d.getFullYear() + '-' + (d.getMonth() + 1) + '-' + d.getDate();

export default {
  name: 'IdCalendar',
  props: {
    modelValue: { type: String, default: '' },
    holidayKeys: { type: Array, default: () => [] },
    markKeys: { type: Array, default: () => [] },
    // holidays with descriptions: [{ holidaydate: 'YYYY-MM-DD', description }]. Used to MARK
    // holiday days + show the description tooltip + list this month's holidays below the grid.
    // Independent of holidayKeys (which DISABLES days) — pass this without holidayKeys to show
    // holiday details WITHOUT blocking the dates (View Classes / Dashboard).
    holidays: { type: Array, default: () => [] },
    disableMondays: { type: Boolean, default: true },
    minDate: { type: Date, default: null },
    // tooltip ของวันที่ถูกปิดเพราะอยู่ก่อน :min-date — แต่ละจอปิดคนละเหตุผล (จองคลาส = หมดเขต
    // รับจอง, วันหมดอายุ = ต้องไม่ก่อนวันเริ่มต้น) จอที่ไม่ส่งมาจะได้ข้อความกลางที่จริงเสมอ
    // ห้าม hardcode ข้อความ "หมดเขตรับจอง" ไว้ตรงนี้ — เคยไปโผล่บนปฏิทินที่ไม่เกี่ยวกับการจอง
    pastReason: { type: String, default: '' },
    // multi-select (จองหลายวัน): parent owns the selected-keys array; the calendar only
    // highlights them and emits 'toggle-date' per click. modelValue is ignored in this mode.
    multi: { type: Boolean, default: false },
    selectedKeys: { type: Array, default: () => [] },
  },
  // month-change fires when the user pages the displayed month (‹ › or the month/year picker),
  // so a parent can re-fetch data for the newly shown month. Purely additive — callers that
  // don't listen are unaffected. Week layout is Monday-first system-wide.
  emits: ['update:modelValue', 'month-change', 'toggle-date'],
  data() {
    const init = this.modelValue ? this.parse(this.modelValue) : new Date();
    return {
      vy: init.getFullYear(), vm: init.getMonth(),
      picker: false, pickYear: init.getFullYear(),
    };
  },
  computed: {
    // locale-aware (recompute when i18nState.locale changes, via the utils)
    monthLabel() { return formatMonthYear(new Date(this.vy, this.vm, 1)); },
    dows() { return weekdayShortNames(); },
    monthsShort() { return monthShortNames(); },
    // lowest selectable year/month from minDate (null = no lower bound)
    minYear() { return this.minTime === null ? null : new Date(this.minTime).getFullYear(); },
    minMonth() { return this.minTime === null ? null : new Date(this.minTime).getMonth(); },
    canPrevYear() { return this.minYear === null || this.pickYear > this.minYear; },
    todayKey() { return keyOf(new Date()); },
    minTime() {
      if (!this.minDate) return null;
      return new Date(this.minDate.getFullYear(), this.minDate.getMonth(), this.minDate.getDate()).getTime();
    },
    canPrev() {
      if (this.minTime === null) return true;
      const m = new Date(this.minTime);
      return this.vy > m.getFullYear() || (this.vy === m.getFullYear() && this.vm > m.getMonth());
    },
    // key ('YYYY-M-D') -> holiday description, built from the holidays prop
    holidayMap() {
      const m = {};
      for (const h of (this.holidays || [])) {
        if (h && h.holidaydate) m[keyOf(this.parse(h.holidaydate))] = h.description || '';
      }
      return m;
    },
    cells() {
      // Monday-first: getDay() is 0=Sun..6=Sat; shift so Monday=0 … Sunday=6
      const offset = (new Date(this.vy, this.vm, 1).getDay() + 6) % 7;
      const days = new Date(this.vy, this.vm + 1, 0).getDate();
      const arr = [];
      for (let i = 0; i < offset; i++) arr.push(null);
      for (let n = 1; n <= days; n++) {
        const d = new Date(this.vy, this.vm, n);
        const k = keyOf(d);
        const past = this.minTime !== null && d.getTime() < this.minTime;
        arr.push({
          n,
          key: k,
          mon: d.getDay() === 1,
          past,
          disabled: (this.disableMondays && d.getDay() === 1) || this.holidayKeys.includes(k) || past,
          today: k === this.todayKey,
          mark: this.markKeys.includes(k),
          holiday: this.holidayMap[k] !== undefined,
          holidayDesc: this.holidayMap[k] || '',
        });
      }
      return arr;
    },
    // holidays that fall in the currently-displayed month, sorted — for the list below the grid
    monthHolidays() {
      return (this.holidays || [])
        .filter(h => h && h.holidaydate)
        .map(h => ({ d: this.parse(h.holidaydate), description: h.description || '' }))
        .filter(h => h.d.getFullYear() === this.vy && h.d.getMonth() === this.vm)
        .sort((a, b) => a.d - b.d)
        .map(h => ({ key: keyOf(h.d), day: h.d.getDate(), description: h.description }));
    },
  },
  watch: {
    // follow the selected value when a PARENT changes it to another month (e.g. a "today"
    // jump or programmatic set) so the displayed month tracks the model. Silent — does NOT
    // emit month-change (that's only for user-initiated ‹ › paging), so no feedback loop.
    modelValue(v) {
      if (!v) return;
      const d = this.parse(v);
      if (d.getFullYear() !== this.vy || d.getMonth() !== this.vm) {
        this.vy = d.getFullYear();
        this.vm = d.getMonth();
      }
    },
  },
  methods: {
    dispYear(y) { return displayYear(y); },
    parse(k) { const p = k.split('-'); return new Date(+p[0], +p[1] - 1, +p[2]); },
    emitMonthChange() { this.$emit('month-change', { year: this.vy, month: this.vm }); },
    prev() { if (!this.canPrev) return; if (this.vm === 0) { this.vm = 11; this.vy--; } else this.vm--; this.emitMonthChange(); },
    next() { if (this.vm === 11) { this.vm = 0; this.vy++; } else this.vm++; this.emitMonthChange(); },
    pick(c) {
      if (!c || c.disabled) return;
      if (this.multi) { this.$emit('toggle-date', c.key); return; }
      this.$emit('update:modelValue', c.key);
    },
    togglePicker() { this.pickYear = this.vy; this.picker = !this.picker; },
    // a month is unreachable when its whole span sits before minDate
    monthDisabled(i) { return this.minYear !== null && (this.pickYear < this.minYear || (this.pickYear === this.minYear && i < this.minMonth)); },
    chooseMonth(i) { if (this.monthDisabled(i)) return; this.vy = this.pickYear; this.vm = i; this.picker = false; this.emitMonthChange(); },
  },
};
</script>

<style scoped>
/* Cap the calendar width so the aspect-ratio:1 cells stay a comfortable square size
   even when the card is full-width on a single-column layout (otherwise cells balloon).
   The card itself stays full-width; only the calendar grid is centered + capped. */
.idcal {
  max-width: 420px;
  margin: 0 auto;
}
/* prev arrow when the calendar can't page earlier than min-date */
.cal-nav-off { opacity: .28; pointer-events: none; cursor: default; }

/* clickable month/year label that opens the quick picker — text-only affordance
   (no filled chip: on touch screens :hover sticks and looked like a doubled background) */
.cal-nav-label {
  display: inline-flex; align-items: center; gap: 4px;
  background: transparent; border: 0; cursor: pointer;
  font-family: var(--font-head); font-weight: 700; font-size: inherit; color: inherit;
  padding: 2px 4px; transition: color var(--dur-fast) var(--ease);
}
.cal-nav-label:hover { color: var(--c-primary); }
.cal-nav-label .mdi { font-size: 17px; color: var(--c-primary); }

/* quick month/year picker panel */
.cal-picker {
  margin: 6px auto 4px; padding: 8px;
  background: var(--c-surface-2); border: 1px solid var(--c-border); border-radius: var(--radius);
}
.cal-picker-yr {
  display: flex; align-items: center; justify-content: center; gap: 14px;
  margin-bottom: 8px; font-family: var(--font-head); font-weight: 700; color: var(--c-text-heading);
}
.cal-picker-yr .mdi { cursor: pointer; font-size: 22px; color: var(--c-primary); }
.cal-picker-yr-lbl { min-width: 52px; text-align: center; }
.cal-picker-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
.cal-mon {
  padding: 9px 4px; border: 1px solid var(--c-border); border-radius: var(--radius-sm);
  background: var(--c-surface); color: var(--c-text-body); font-family: var(--font-body); font-size: 13px;
  cursor: pointer; transition: background var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease), border-color var(--dur-fast) var(--ease);
}
.cal-mon:hover:not(.disabled) { border-color: var(--c-primary); color: var(--c-primary); }
.cal-mon.on { background: var(--c-primary); border-color: var(--c-primary); color: #fff; font-weight: 700; }
.cal-mon.disabled { opacity: .35; cursor: default; }

/* holiday marking (red dot + red number when not disabled) + this-month holiday list */
.cal-dot-hol { background: var(--c-error); }
.cal-cell.holiday:not(.on):not(.disabled) { color: var(--c-error); }
.cal-holidays {
  margin-top: 12px; padding-top: 10px; border-top: 1px dashed var(--c-border);
  display: flex; flex-direction: column; gap: 6px;
}
.cal-holidays-h {
  display: flex; align-items: center; gap: 6px;
  font-family: var(--font-head); font-weight: 700; font-size: 12.5px; color: var(--c-text-heading);
}
.cal-holidays-h .mdi { color: var(--c-error); font-size: 16px; }
.cal-hol-row { display: flex; align-items: center; gap: 8px; font-size: 12.5px; }
.cal-hol-day {
  flex: 0 0 26px; width: 26px; height: 22px; padding: 0;
  display: inline-grid; place-items: center; border-radius: 7px;
  background: var(--c-error); color: #fff; font-weight: 700; font-size: 12px;
}
.cal-hol-desc { color: var(--c-text-body); overflow-wrap: anywhere; }
</style>
